import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  MenuItem,
  Grid,
  Typography,
  InputAdornment
} from '@mui/material';

export default function PromotionForm({ promotion, onSave, onCancel }) {
  const [name, setName] = useState('');
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState('percent');
  const [discountValue, setDiscountValue] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [usageLimit, setUsageLimit] = useState('');
  const [appliesTo, setAppliesTo] = useState('items');
  const [minPurchase, setMinPurchase] = useState('0');

  useEffect(() => {
    if (promotion) {
      setName(promotion.name);
      setCode(promotion.code);
      setDiscountType(promotion.discount_type);
      setDiscountValue(promotion.discount_value.toString());
      setStartDate(promotion.start_date.split('T')[0]);
      setEndDate(promotion.end_date.split('T')[0]);
      setUsageLimit(promotion.usage_limit?.toString() || '');
      setAppliesTo(promotion.applies_to);
      setMinPurchase(promotion.min_purchase?.toString() || '0');
    } else {
      // Set default dates
      const today = new Date().toISOString().split('T')[0];
      const nextMonth = new Date();
      nextMonth.setMonth(nextMonth.getMonth() + 1);
      setStartDate(today);
      setEndDate(nextMonth.toISOString().split('T')[0]);
    }
  }, [promotion]);

  const handleSave = () => {
    if (!name || !code || !discountValue || !startDate || !endDate) {
      alert('Please fill in all required fields');
      return;
    }

    if (parseFloat(discountValue) <= 0) {
      alert('Discount value must be greater than 0');
      return;
    }

    if (discountType === 'percent' && parseFloat(discountValue) > 100) {
      alert('Percentage discount cannot exceed 100%');
      return;
    }

    if (new Date(endDate) <= new Date(startDate)) {
      alert('End date must be after start date');
      return;
    }

    onSave({
      name,
      code: code.toUpperCase(),
      discount_type: discountType,
      discount_value: parseFloat(discountValue),
      start_date: new Date(startDate).toISOString(),
      end_date: new Date(endDate).toISOString(),
      usage_limit: usageLimit ? parseInt(usageLimit) : null,
      applies_to: appliesTo,
      min_purchase: parseFloat(minPurchase)
    });
  };

  const generateRandomCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 8; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCode(result);
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Promotion Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder="e.g., Summer Sale 2024"
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Promo Code"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            required
            placeholder="e.g., SUMMER2024"
            inputProps={{ style: { textTransform: 'uppercase' } }}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            variant="outlined"
            onClick={generateRandomCode}
            sx={{ height: '56px' }}
          >
            Generate Code
          </Button>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            select
            label="Discount Type"
            value={discountType}
            onChange={(e) => setDiscountType(e.target.value)}
          >
            <MenuItem value="percent">Percentage</MenuItem>
            <MenuItem value="fixed">Fixed Amount</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Discount Value"
            value={discountValue}
            onChange={(e) => setDiscountValue(e.target.value)}
            required
            InputProps={{
              startAdornment: discountType === 'fixed' ? (
                <InputAdornment position="start">$</InputAdornment>
              ) : null,
              endAdornment: discountType === 'percent' ? (
                <InputAdornment position="end">%</InputAdornment>
              ) : null
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="Start Date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="date"
            label="End Date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Usage Limit"
            value={usageLimit}
            onChange={(e) => setUsageLimit(e.target.value)}
            placeholder="Leave empty for unlimited"
            helperText="Maximum number of times this code can be used"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            type="number"
            label="Minimum Purchase"
            value={minPurchase}
            onChange={(e) => setMinPurchase(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>
            }}
            helperText="Minimum purchase amount required"
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            select
            label="Applies To"
            value={appliesTo}
            onChange={(e) => setAppliesTo(e.target.value)}
          >
            <MenuItem value="items">Individual Items</MenuItem>
            <MenuItem value="sales">Entire Sales</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSave}>
              Save Promotion
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
